import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env';
import { map } from 'rxjs/operators';
import { SmsPreferences } from '@models/sms/preferences';
import { compare } from 'fast-json-patch';
import { flattenObject } from '@utils/utils';
import { MessageType, MessageTypes } from '@models/sms/messagetype';
import { SmsTemplate } from '@models/sms/templates';

@Injectable({
  providedIn: 'root'
})
export class SmsService {
  private readonly baseUrl;
  private messageTypesRequested = false;
  private messageTypesSubject$ = new ReplaySubject<any>();

  constructor(private http: HttpClient) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getPreferences(key?: string): Observable<SmsPreferences> {
    if (key) {
      const smskey = key;
      return this.http.get<Response>(this.baseUrl + 'sms-preferences').pipe(map(data => SmsPreferences.deserialize(data[smskey])));
    }
    return this.http.get<Response>(this.baseUrl + 'sms-preferences').pipe(map(data => SmsPreferences.deserialize(data)));
  }

  updatePreferences(previous: SmsPreferences, toSave: SmsPreferences): Observable<SmsPreferences> {
    const patch = compare(flattenObject(previous, true), flattenObject(toSave, true));
    // console.log("previous",flattenObject(previous, true))
    // console.log("toSave",flattenObject(toSave, true))
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    // const smskey = 'sms';
    // console.log("patch",patch)
    return this.http
      .patch<Response>(this.baseUrl + 'sms-preferences', patch, { headers: patchHeaders })
      .pipe(map(data => SmsPreferences.deserialize(data)));
  }

  getMessageTypes(force: boolean = false): Observable<MessageTypes> {
    if (!this.messageTypesRequested || force) {
      this.messageTypesRequested = true;
      this.http
        .get<Response>(this.baseUrl + 'message-types')
        .pipe(
          map(data => {
            return MessageTypes.deserialize({
              data: (data ? ((data as unknown) as Array<string>) : []).map(data2 => MessageType.deserialize(data2))
            });
          })
        )
        .subscribe(x => this.messageTypesSubject$.next(x));
    }
    return this.messageTypesSubject$;
  }

  getSmsTemplateList(): Observable<Array<SmsTemplate>> {
    return this.http
      .get<Array<SmsTemplate>>(this.baseUrl + 'sms/template')
      .pipe(map(templates => templates.map(template => SmsTemplate.deserialize(template))));
  }

  getSmsTemplate(id: number): Observable<SmsTemplate> {
    return this.http.get<SmsTemplate>(this.baseUrl + `sms/template/${id}/`).pipe(map(template => SmsTemplate.deserialize(template)));
  }

  deleteSmsTemplate(id: number): Observable<void> {
    return this.http.delete<void>(this.baseUrl + `sms/template/${id}/`);
  }

  createSmsTemplate(template: SmsTemplate): Observable<SmsTemplate> {
    return this.http
      .post<SmsTemplate>(this.baseUrl + `sms/template/`, template)
      .pipe(map(createdTemplate => SmsTemplate.deserialize(createdTemplate)));
  }

  updateSmsTemplate(previous: SmsTemplate, toSave: SmsTemplate): Observable<SmsTemplate> {
    const patch = compare(flattenObject(previous, true), flattenObject(toSave, true));
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');

    return this.http
      .patch<Response>(this.baseUrl + `sms/template/${previous.idTemplate}`, patch, { headers: patchHeaders })
      .pipe(map(updatedSmsTemplate => SmsPreferences.deserialize(updatedSmsTemplate)));
  }
}
